import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2, CheckCircle, XCircle, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";

import { useCategories } from "@/hooks/use-categories";
import { ImageUpload } from "@/components/ImageUpload";

const TOWNS = ["Ayr","Kilmarnock","Irvine","Prestwick","Troon","Saltcoats","Ardrossan","Largs","Kilwinning","Stewarton","Maybole","Girvan","Cumnock","Auchinleck","Newmilns","Kilbirnie","Beith","Dalry"];

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const EMPTY = {
  businessName: "", title: "", description: "", category: "", town: "Ayr",
  imageUrl: "", startDate: "", endDate: "", redemptionInstructions: "",
};

interface DealItem {
  id: number; businessName: string; title: string; description: string;
  category: string; town: string; imageUrl: string | null;
  startDate: string | null; endDate: string | null;
  redemptionInstructions: string | null;
  approved: boolean; featured: boolean; sponsored: boolean; createdAt: string;
}

export default function AdminDeals() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [items, setItems] = useState<DealItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<DealItem | null>(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const dealCategories = useCategories("deal");
  useEffect(() => {
    if (!form.category && dealCategories.length > 0) setForm(f => ({ ...f, category: dealCategories[0]! }));
  }, [dealCategories]);

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/deals/all", { headers: authHeaders() });
      const data = await res.json() as { items: DealItem[] };
      setItems(data.items ?? []);
    } catch {
      toast({ title: "Failed to load deals", variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));
  const f = (k: keyof typeof EMPTY) => form[k];

  const openEdit = (item: DealItem) => {
    setEditing(item);
    setShowCreate(false);
    setForm({
      businessName: item.businessName, title: item.title, description: item.description,
      category: item.category, town: item.town, imageUrl: item.imageUrl ?? "",
      startDate: item.startDate ?? "", endDate: item.endDate ?? "",
      redemptionInstructions: item.redemptionInstructions ?? "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCreate = async () => {
    if (!form.businessName || !form.title || !form.description) {
      toast({ title: "Business name, title and description are required", variant: "destructive" }); return;
    }
    setSaving(true);
    try {
      const r = await fetch("/api/admin/deals", {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          businessName: form.businessName, title: form.title, description: form.description,
          category: form.category, town: form.town,
          imageUrl: form.imageUrl || null,
          startDate: form.startDate || null, endDate: form.endDate || null,
          redemptionInstructions: form.redemptionInstructions || null,
        }),
      });
      if (!r.ok) { const d = await r.json() as { error?: string }; throw new Error(d.error ?? "Failed"); }
      toast({ title: "Deal created and approved" });
      setForm({ ...EMPTY }); setShowCreate(false);
      void load();
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : "Failed to create", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const r = await fetch(`/api/deals/${editing.id}`, {
        method: "PUT", headers: authHeaders(),
        body: JSON.stringify({
          businessName: form.businessName, title: form.title, description: form.description,
          category: form.category, town: form.town,
          imageUrl: form.imageUrl || null,
          startDate: form.startDate || null, endDate: form.endDate || null,
          redemptionInstructions: form.redemptionInstructions || null,
        }),
      });
      if (!r.ok) throw new Error("Failed");
      toast({ title: "Deal updated" });
      setEditing(null); setForm({ ...EMPTY });
      void load();
    } catch {
      toast({ title: "Failed to save", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this deal? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await fetch(`/api/deals/${id}`, { method: "DELETE", headers: authHeaders() });
      setItems(prev => prev.filter(i => i.id !== id));
      toast({ title: "Deal deleted" });
    } catch {
      toast({ title: "Failed to delete", variant: "destructive" });
    } finally { setDeletingId(null); }
  };

  const toggleApproved = async (item: DealItem) => {
    try {
      await fetch(`/api/deals/${item.id}/approve`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ approved: !item.approved }),
      });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, approved: !i.approved } : i));
    } catch {
      toast({ title: "Failed to update", variant: "destructive" });
    }
  };

  const filtered = items.filter(i =>
    !search || i.title.toLowerCase().includes(search.toLowerCase()) ||
    i.businessName.toLowerCase().includes(search.toLowerCase()) ||
    i.town.toLowerCase().includes(search.toLowerCase())
  );

  const isFormOpen = showCreate || editing !== null;

  return (
    <AdminLayout current="/admin/deals">
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-primary">Deals</h1>
            <p className="text-sm text-slate-500">{items.length} total · {items.filter(i => !i.approved).length} pending</p>
          </div>
          {!isFormOpen && (
            <button
              onClick={() => { setShowCreate(true); setEditing(null); setForm({ ...EMPTY }); }}
              className="flex items-center gap-2 bg-primary text-white font-bold px-4 py-2.5 rounded-xl text-sm hover:bg-primary/90 transition-colors"
            >
              <Plus size={16} /> Add Deal
            </button>
          )}
        </div>

        {/* Create / Edit form */}
        {isFormOpen && (
          <Card>
            <CardContent className="pt-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-primary text-lg">{editing ? "Edit Deal" : "New Deal"}</h2>
                <button onClick={() => { setShowCreate(false); setEditing(null); setForm({ ...EMPTY }); }} className="text-slate-400 hover:text-slate-600 text-xl font-bold">×</button>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Business Name *</label>
                  <Input value={f("businessName")} onChange={e => set("businessName", e.target.value)} placeholder="e.g. The Corner Café" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Deal Title *</label>
                  <Input value={f("title")} onChange={e => set("title", e.target.value)} placeholder="e.g. 20% Off All Meals" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Category</label>
                  <select value={f("category")} onChange={e => set("category", e.target.value)} className="w-full h-10 text-sm border border-input rounded-md px-3 bg-background">
                    {dealCategories.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Town</label>
                  <select value={f("town")} onChange={e => set("town", e.target.value)} className="w-full h-10 text-sm border border-input rounded-md px-3 bg-background">
                    {TOWNS.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
                  <Input type="date" value={f("startDate")} onChange={e => set("startDate", e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
                  <Input type="date" value={f("endDate")} onChange={e => set("endDate", e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Image</label>
                  <ImageUpload value={f("imageUrl")} onChange={url => set("imageUrl", url)} stockCategory="deal" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Description *</label>
                  <Textarea value={f("description")} onChange={e => set("description", e.target.value)} rows={3} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Redemption Instructions</label>
                  <Textarea value={f("redemptionInstructions")} onChange={e => set("redemptionInstructions", e.target.value)} rows={2} placeholder="How customers can claim this deal…" />
                </div>
              </div>
              <div className="flex gap-3 pt-2 border-t border-slate-100">
                <Button disabled={saving} onClick={editing ? handleEdit : handleCreate} className="bg-primary hover:bg-primary/90">
                  {saving ? "Saving…" : editing ? "Save Changes" : "Create Deal"}
                </Button>
                <Button variant="outline" onClick={() => { setShowCreate(false); setEditing(null); setForm({ ...EMPTY }); }}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by title, business or town…"
          className="w-full max-w-sm border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary/40"
        />

        {/* List */}
        {loading ? (
          <div className="text-slate-400 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-slate-400 text-sm">No deals found.</div>
        ) : (
          <div className="space-y-2">
            {filtered.map(item => (
              <div key={item.id} className="bg-white border border-slate-200 rounded-xl px-5 py-4 flex gap-4 items-start">
                {item.imageUrl && <img src={item.imageUrl} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800 text-sm leading-tight">{item.title}</span>
                    <span className="text-xs text-slate-400 font-medium">{item.businessName}</span>
                    {item.featured && <span className="text-xs font-bold text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full flex items-center gap-1"><Star size={10} /> Featured</span>}
                    {item.sponsored && <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">Boosted</span>}
                    {!item.approved && <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">Pending</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-slate-400">
                    <span>{item.town}</span><span>·</span><span>{item.category}</span>
                    {item.endDate && <><span>·</span><span>Ends {item.endDate}</span></>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-1">{item.description}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => void toggleApproved(item)} title={item.approved ? "Approved — click to unpublish" : "Pending — click to approve"}
                    className={`p-1.5 rounded-lg transition-colors ${item.approved ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-100"}`}>
                    {item.approved ? <CheckCircle size={18} /> : <XCircle size={18} />}
                  </button>
                  <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors" title="Edit">
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => void handleDelete(item.id)} disabled={deletingId === item.id}
                    className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors disabled:opacity-50" title="Delete">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
